import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import { CSVLink } from 'react-csv';

import VerbButton, { VERB_BUTTON_TYPE } from '../VerbButton';
import DropzoneFileUpload from '../formComponents/inputs/DropzoneFileUpload';
import SleekLoadingIndicator from '../SleekLoadingIndicator';
import {
    csvToArray,
    validateEmail,
    validFileWithExpectedHeaders,
    validFileWithExpectedRow,
    colName
} from '../../utils/StringUtils';
import { ERROR_MSG } from '../../Constants';

import { hideModal } from '../../actions/index';

const fieldsToValidate = {
    'Email Addresses': {
        required: true,
        validate: (value) => validateEmail(value),
        column: 'A',
        invalidValueError: ERROR_MSG.EMAIL
    }
};

class BulkInviteEmployees extends Component {
    static propTypes = {
        handleSubmit: PropTypes.func.isRequired,
        hideModal: PropTypes.func,
        enableInviteByEmail: PropTypes.bool,
        toggleConfirmationModal: PropTypes.func
    };

    constructor(props) {
        super(props);

        this.state = {
            isValidating: false,
            file: null,
            fileName: null,
            fileValid: false,
            errors: {},
            isSubmitting: false
        };
    }

    componentWillUnmount() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
    }

    cancelOrClose = () => {
        const { file } = this.state;
        const { toggleConfirmationModal } = this.props;
        if (file && toggleConfirmationModal) {
            const modalInfo = {
                title: 'Cancel Bulk Invite?',
                messageTextProp: {
                    messageId: 'BULK_INVITE_CANCEL',
                    defaultMessage: 'Changes you made will not be saved.'
                },
                cancelButtonProp: {
                    textId: 'BUTTON_CANCEL',
                    defaultMessage: 'CANCEL'
                },
                actionButtonProp: {
                    textId: 'BUTTON_LEAVE',
                    defaultMessage: 'LEAVE'
                },
                isOpen: true,
                cancelFunc: () => toggleConfirmationModal({ isOpen: false }),
                actionFunc: () => {
                    toggleConfirmationModal({ isOpen: false });
                    this.props.hideModal();
                }
            };
            toggleConfirmationModal(modalInfo);
        } else {
            this.props.hideModal();
        }
    };

    onFileUpload = (fileObject) => {
        this.setState(() => ({
            file: fileObject,
            fileName: fileObject.name,
            isValidating: true,
            errors: {}
        }));

        return new Promise(() => {
            const csv = csvToArray(fileObject.file);
            const headers = csv[0];
            const rows = csv.splice(1);

            // validate headers
            const { fileValid, errors } = validFileWithExpectedHeaders(headers, fieldsToValidate);
            if (!fileValid) {
                this.setState({
                    isValidating: false,
                    errors
                });
                return Promise.resolve();
            }

            const rowErrorMessage = {};
            const invalidDataErrorObject = {};
            const usersToInvite = {};

            // eslint-disable-next-line no-restricted-syntax
            for (const [index, userRow] of rows.entries()) {
                const userRowObject = {};
                // eslint-disable-next-line no-loop-func
                userRow.forEach((value, userColumnIndex) => {
                    const columnName = colName(userColumnIndex);
                    if (value) {
                        userRowObject[headers[userColumnIndex]] = value;
                        if (columnName !== 'A' && (value || headers[userColumnIndex])) {
                            invalidDataErrorObject[columnName] = true;
                        }
                    }
                });
                const userEmail = userRow[0];

                const { fileValid, errors } = validFileWithExpectedRow(userRowObject, headers, index, fieldsToValidate);
                if (!fileValid || usersToInvite[userEmail]) {
                    const concatRows = errors[ERROR_MSG.EMAIL].length ? errors[ERROR_MSG.EMAIL] : [index + 2];
                    if (rowErrorMessage[ERROR_MSG.EMAIL]) {
                        rowErrorMessage[ERROR_MSG.EMAIL].errors =
                            rowErrorMessage[ERROR_MSG.EMAIL].errors.concat(concatRows);
                    } else {
                        rowErrorMessage[ERROR_MSG.EMAIL] = {
                            type: 'Row(s)',
                            errors: concatRows
                        };
                    }
                }
                if (userEmail) {
                    usersToInvite[userEmail] = userEmail;
                }
            }
            if (!isEmpty(rowErrorMessage) || !isEmpty(invalidDataErrorObject)) {
                let invalidErrorMessage = {};
                if (invalidDataErrorObject && !isEmpty(invalidDataErrorObject)) {
                    invalidErrorMessage = {
                        'Invalid Data': {
                            type: 'Column(s)',
                            errors: Object.keys(invalidDataErrorObject)
                        }
                    };
                }
                this.setState({
                    isValidating: false,
                    errors: {
                        ...rowErrorMessage,
                        ...invalidErrorMessage
                    }
                });

                return Promise.resolve();
            }

            return setTimeout(() => {
                this.setState({
                    isValidating: false,
                    fileValid: true,
                    usersToInvite: Object.values(usersToInvite)
                });
                return Promise.resolve();
            }, 3000);
        });
    };

    getDropZoneImageAndText = () => {
        const { isValidating, file, fileValid, fileName, usersToInvite = [] } = this.state;
        let dropzoneImage = <i className={'fal fa-file-csv dropzone-image fa-8x'} />;
        let dropzoneText = 'Select a CSV file to upload';

        if (isValidating) {
            dropzoneImage = <SleekLoadingIndicator className={'dropzone-file-loading'} />;
            dropzoneText = 'Uploading...';
        }

        if (file && !isValidating) {
            dropzoneText = 'Upload a different CSV file';
            if (!fileValid) {
                dropzoneImage = (
                    <div>
                        <i className={'fal fa-times dropzone-image fa-7x critical'} />
                        <p>Invalid CSV - See error message below.</p>
                    </div>
                );
            } else {
                dropzoneImage = (
                    <div>
                        <i className={'fal fa-check dropzone-image fa-8x success'} />
                        <p>
                            {fileName} - {usersToInvite.length} emails uploaded
                        </p>
                    </div>
                );
            }
        }
        return {
            dropzoneImage,
            dropzoneText
        };
    };

    bulkInviteMembers = () => {
        const { usersToInvite = [] } = this.state;
        const { handleSubmit, hideModal } = this.props;
        this.setState({
            isSubmitting: true
        });
        return handleSubmit(usersToInvite)
            .then(() => {
                this.setState({
                    isSubmitting: false
                });
                hideModal();
            })
            .catch(() => {
                this.setState({
                    isSubmitting: false
                });
                hideModal();
            });
    };

    render() {
        const { isValidating, file, fileValid, usersToInvite = [], errors, isSubmitting } = this.state;
        const { enableInviteByEmail } = this.props;
        const accept = 'text/csv'; // only accepts csv

        const { dropzoneText, dropzoneImage } = this.getDropZoneImageAndText();

        return (
            <div className={'bulk-invite-container'}>
                {isSubmitting ? (
                    <div className={'submit-bulk-invite'}>
                        <SleekLoadingIndicator />
                    </div>
                ) : null}
                <div className={'row'}>
                    <div>
                        {/* <DropzoneFileUpload
                            accept={accept}
                            className={'row'}
                            dropzoneClass={`col-sm-12 ${isValidating ? 'validating-dropzone' : 'upload-enabled'}`}
                            onUpload={isValidating || isSubmitting ? null : this.onFileUpload}
                            text={dropzoneText}
                            disabled={isValidating || isSubmitting}
                            readAsText={true}
                            image={dropzoneImage}
                        /> */}
                    </div>
                </div>
                {file && !fileValid && !isEmpty(errors) ? (
                    <div className={'row'}>
                        <div className={'errors-container'}>
                            {Object.keys(errors).map((errorType) => (
                                <div className={'specific-errors'}>
                                    {errorType}
                                    <div>{`${errors[errorType].type} ${errors[errorType].errors.join(', ')}.`}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}
                <div className={'row'}>
                    <p className={'upload-instructions'}>
                        Your CSV file must be formatted to include:
                        <ul>
                            <li>Cell 1A is titled "Email Addresses"</li>
                            <li>Email addresses are valid</li>
                            <li>Emails are in column A</li>
                            <li>Emails are individually separated by row</li>
                        </ul>
                    </p>
                </div>
                <div className={'row'}>
                    <CSVLink
                        className={'template-download'}
                        data={[
                            ['Email Addresses'],
                            ['firstname.lastname@company.com'],
                            ['firstinitial.lastname@company.com'],
                            ['name@company.com'],
                            ['jane.doe@doremi.com'],
                            ['johnsmith@abcindustries.com']
                        ]}
                        filename={'Bulk_Invite_Learners_Template'}
                    >
                        Download this template CSV to avoid errors.
                    </CSVLink>
                </div>
                <div className={'row'}>
                    <div className={'col-sm-12 buttons-container'}>
                        {enableInviteByEmail && (
                            <>
                                <VerbButton
                                    className={'bulkInvite-cancel'}
                                    buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                                    onClick={this.cancelOrClose}
                                    textId={'BUTTON_CANCEL'}
                                    defaultMessage={'CANCEL'}
                                />
                                <VerbButton
                                    className={'bulkInvite-submit'}
                                    onClick={this.bulkInviteMembers}
                                    disabled={!file || !fileValid}
                                    buttonText={`INVITE ${
                                        usersToInvite.length ? `${usersToInvite.length} ` : ''
                                    }MEMBERS`}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

const mapDispatchToProps = (dispatch) => ({
    hideModal: () => dispatch(hideModal())
});

export default connect(null, mapDispatchToProps)(BulkInviteEmployees);
